import React, { useState } from 'react';
import { useAgencyUsers } from '../../hooks/useAgencyUsers';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { Trash2, Plus } from 'lucide-react';
import ScrollArea from '../ui/shared/ScrollArea';
import Table from '../ui/shared/table/Table';
import TableHeader from '../ui/shared/table/TableHeader';
import TableBody from '../ui/shared/table/TableBody';
import TableRow from '../ui/shared/table/TableRow';
import TableCell from '../ui/shared/table/TableCell';
import { format } from 'date-fns';

export function AgencyUsers() {
  const { users, loading, error, addUser, removeUser } = useAgencyUsers();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addSuccess, setAddSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setAddError('Email inválido');
      return;
    }

    if (!name || name.trim().length === 0) {
      setAddError('Nome é obrigatório');
      return;
    }

    setAdding(true);
    setAddError(null);
    setAddSuccess(null);

    try {
      const result = await addUser(name.trim(), email.trim());
      setAddSuccess(result.message);
      setName('');
      setEmail('');
      setTimeout(() => {
        setShowAddDialog(false);
        setAddSuccess(null);
      }, 2000);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Erro ao adicionar usuário');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (userId: string, userName: string) => {
    if (!confirm(`Deseja realmente remover ${userName} da agência?`)) {
      return;
    }

    try {
      await removeUser(userId);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erro ao remover usuário');
    }
  };

  const handleDialogChange = (open: boolean) => {
    setShowAddDialog(open);
    if (!open) {
      setName('');
      setEmail('');
      setAddError(null);
      setAddSuccess(null);
    }
  };

  const filteredUsers = users.filter((user) => {
    const search = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-neutral-600 border-t-neutral-50 rounded-full animate-spin"></div>
          <p className="text-neutral-50">Carregando usuários...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-neutral-50 mb-2">Erro ao carregar usuários</h3>
          <p className="text-neutral-300 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="w-full mt-3 sm:mt-4 flex-wrap gap-3 flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Pesquisar usuários..."
            className="w-[170px] sm:w-[220px] md:w-[270px] xl:w-[417px] min-h-[40px] rounded-[6px] placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Dialog open={showAddDialog} onOpenChange={handleDialogChange}>
            <DialogTrigger asChild>
              <button className="bg-[#E4E4E7] min-h-[40px] text-black font-medium text-[14px] px-3 rounded-[6px] flex justify-center items-center gap-2 hover:bg-[#D4D4D8] transition-colors">
                <Plus className="w-4 h-4" />
                Adicionar Usuário
              </button>
            </DialogTrigger>
            <DialogContent className="flex w-full max-w-md p-0 border-[#3F3F464D] bg-[#18181B] flex-col">
              <ScrollArea classNames={{ root: "max-h-[90dvh] p-5 overflow-auto" }}>
                <h3 className="text-xl font-semibold text-neutral-50 mb-6">
                  Adicionar Usuário à Agência
                </h3>

                <form onSubmit={handleAddUser} className="w-full gap-4 flex flex-col">
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name" className="text-white">
                      Nome Completo
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setAddError(null);
                      }}
                      placeholder="João Silva"
                      className="bg-neutral-600 text-white placeholder:text-[#71717A] min-h-[40px] border border-[#3F3F46]"
                      disabled={adding}
                      required
                    />
                  </div>

                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="email" className="text-white">
                      E-mail
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setAddError(null);
                      }}
                      placeholder="joao@exemplo.com"
                      className="bg-neutral-600 text-white placeholder:text-[#71717A] min-h-[40px] border border-[#3F3F46]"
                      disabled={adding}
                      required
                    />
                    <p className="text-xs text-neutral-400">
                      O usuário será adicionado como administrador da agência.
                      {' '}Se o usuário não existir, será criado automaticamente e receberá um e-mail de boas-vindas com a senha de acesso.
                    </p>
                  </div>

                  {addError && (
                    <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-3 py-2 rounded text-sm">
                      {addError}
                    </div>
                  )}

                  {addSuccess && (
                    <div className="bg-green-500/20 border border-green-500/50 text-green-400 px-3 py-2 rounded text-sm">
                      {addSuccess}
                    </div>
                  )}

                  <div className="flex gap-3 justify-end pt-2">
                    <button
                      type="button"
                      onClick={() => handleDialogChange(false)}
                      disabled={adding}
                      className="border min-h-[40px] text-neutral-50 font-medium text-[14px] px-4 border-neutral-500 bg-neutral-600 rounded-[6px] hover:bg-neutral-700 transition-colors disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={adding || !email || !name}
                      className="bg-[#E4E4E7] min-h-[40px] text-black font-medium text-[14px] px-4 rounded-[6px] hover:bg-[#D4D4D8] transition-colors disabled:opacity-50"
                    >
                      {adding ? 'Adicionando...' : 'Adicionar'}
                    </button>
                  </div>
                </form>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {filteredUsers.length === 0 ? (
        <div className="flex items-center justify-center flex-1">
          <div className="text-center">
            <div className="w-16 h-16 bg-neutral-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="w-8 h-8 text-neutral-400" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-50 mb-2">
              {searchTerm ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado'}
            </h3>
            <p className="text-neutral-400 text-sm mb-4">
              {searchTerm
                ? 'Tente ajustar sua pesquisa'
                : 'Comece adicionando usuários para sua agência'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => setShowAddDialog(true)}
                className="bg-[#E4E4E7] min-h-[40px] text-black font-medium text-[14px] px-4 rounded-[6px] flex justify-center items-center gap-2 hover:bg-[#D4D4D8] transition-colors mx-auto"
              >
                <Plus className="w-4 h-4" />
                Adicionar Primeiro Usuário
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-1 min-h-0 w-full overflow-hidden rounded-[6px] border border-[#3F3F464D]">
          <ScrollArea classNames={{ root: "w-full" }}>
            <Table>
              <TableHeader>
                <tr>
                  <th className="text-left text-xs font-medium text-neutral-400 uppercase tracking-wider py-3 px-4">
                    Usuário
                  </th>
                  <th className="text-left text-xs font-medium text-neutral-400 uppercase tracking-wider py-3 px-4">
                    E-mail
                  </th>
                  <th className="text-left text-xs font-medium text-neutral-400 uppercase tracking-wider py-3 px-4">
                    Data de Cadastro
                  </th>
                  <th className="text-right text-xs font-medium text-neutral-400 uppercase tracking-wider py-3 px-4">
                    Ações
                  </th>
                </tr>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">
                            {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div className="text-sm font-medium text-neutral-50">
                          {user.name || 'Usuário'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-neutral-300">
                        {user.email || 'N/A'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-neutral-400">
                        {format(new Date(user.created_at), 'dd/MM/yyyy')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleRemove(user.id, user.name || user.email || 'este usuário')}
                          className="text-red-400 hover:text-red-300 p-2 rounded hover:bg-red-500/10 transition-colors"
                          title="Remover usuário"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
